import React from 'react';
import { useFormContext } from 'react-hook-form';
import { Monitor, Clock, FileVideo, Volume2, ShieldCheck, Plus } from 'lucide-react';
import type { CreativeSlotState, SelfServiceFormData } from '../../../types/selfService';
import { useReusableLibrary } from '../shared/useReusableLibrary';
import LibraryPickerModal, { type LibraryPick } from './LibraryPickerModal';
import ArchivedFileCollisionModal from '../shared/ArchivedFileCollisionModal';
import { StepHeader, accentVars, tintFromAccent } from './_shared';
import { useWaymarkOptional } from '../waymark/context';
import WaymarkPanel from '../waymark/WaymarkPanel';
import WaymarkDisclosure from '../waymark/WaymarkDisclosure';
import type { AiCommercialsState } from '../waymark/useAiCommercials';
import { useWaymarkEditorSession } from '../waymark/useWaymarkEditorSession';
import type { FinishedCommercial } from '../waymark/useAiCommercials';
import { buildReceipt } from '../waymark/receipt';

const EMPTY_FINISHED: FinishedCommercial[] = [];
import CreativeSlotCard from '../scheduler/CreativeSlotCard';
import type { SlotUploadState } from '../scheduler/useCreativeSlots';
import MultiCreativeScheduler from '../scheduler/MultiCreativeScheduler';
import { buildStationPalette } from '../../../lib/dateAxisRendering';
import CoverageBanner from '../shared/CoverageBanner';
import ConcurrencyBanner from '../shared/ConcurrencyBanner';
import { validateCreativeCoverage, type UncoveredRange } from '../shared/creativeCoverage';
import {
  validateCreativeConcurrency,
  type OverCapRange,
} from '../shared/creativeConcurrency';

interface UploadCreativeStepProps {
  // The station slug — needed by /creatives/preview-url to resolve a signed
  // URL for a previously-uploaded storagePath without going through a
  // submission row.
  slug: string;
  accentColor?: string;
  primaryColor?: string;
  secondaryColor?: string;
  campaignStartDate: string;
  campaignEndDate: string;
  // Unified slot state. Slot 0 lives at slots[0]; all operations target a
  // specific slotId. The hook guarantees slots.length >= 1 so the step always
  // has something to render into.
  slots: CreativeSlotState[];
  files: Record<string, File | null>;
  uploadState: Record<string, SlotUploadState>;
  onFileChange: (slotId: string, file: File | null) => void;
  onSlotChange: (slotId: string, patch: Partial<CreativeSlotState>) => void;
  onSlotRemove: (slotId: string) => void;
  onAddSlot: () => void;
  // CTV-476: reset every overlap Rotation Segment to even split. Solo
  // segments stay 100% by construction. The scheduler also clears its
  // per-segment recency stack on reset.
  onResetWeights: () => void;
  // CTV-457 follow-up: the currently selected advertiser seat. When present,
  // and when the seat has reusable prior commercials, each slot gets a
  // "Pick from library" affordance so the user can skip the re-upload.
  advertiserId?: string | null;
  onPickFromLibrary?: (slotId: string, pick: LibraryPick) => void;
  // Waymark "Run this commercial" wiring. CTV-775: commit/replace carry the
  // ACTIVE AI commercial's id; the wizard stamps it on the slot, which is
  // what makes the commercial committed (durably, via the draft).
  onCommitWaymark?: (blob: Blob, videoId: string, commercialId: string | null) => void;
  // CTV-785: a committed commercial was reviewed in a side editor session and
  // its new broadcast file is ready; replace the slot that carries it.
  onReplaceWaymark?: (file: File, commercialId: string) => void;
  // CTV-775: the surface's AI commercials (active id, ordinals, create).
  aiCommercials?: AiCommercialsState;
  onMakeAnother?: () => void | Promise<void>;
  // CTV-775: the station's display name, for the AI commercial FAQ.
  publisherName?: string;
  // CTV-563: when the Waymark render fails our client-side ad-spec check
  // (duration / resolution), the wizard exposes the composed human message
  // here and the panel renders a rejection banner with a Try-again button.
  waymarkRejection?: string | null;
  onClearWaymarkRejection?: () => void;
  // Bubbles per-day coverage state up to the wizard so it can gate the
  // Continue button. Fires whenever the slot set or campaign window changes.
  onCoverageChange?: (uncoveredRanges: UncoveredRange[]) => void;
  // ADR CTV-506: bubbles per-day concurrency cap state up to the wizard so it
  // can gate Continue alongside coverage.
  onConcurrencyChange?: (overCapRanges: OverCapRange[]) => void;
}

const specs = [
  { icon: FileVideo, label: 'Format', value: 'MP4' },
  { icon: Clock, label: 'Duration', value: '15s or 30s' },
  { icon: Monitor, label: 'Resolution', value: '1920×1080 (1080p) min' },
  { icon: Volume2, label: 'Audio', value: 'Required' },
];

const UploadCreativeStep: React.FC<UploadCreativeStepProps> = ({
  slug,
  accentColor = '#3b82f6',
  primaryColor,
  secondaryColor,
  campaignStartDate,
  campaignEndDate,
  slots,
  files,
  uploadState,
  onFileChange,
  onSlotChange,
  onSlotRemove,
  onAddSlot,
  onResetWeights,
  advertiserId,
  onPickFromLibrary,
  onCommitWaymark,
  onReplaceWaymark,
  aiCommercials,
  onMakeAnother,
  publisherName = 'your station',
  waymarkRejection = null,
  onClearWaymarkRejection,
  onCoverageChange,
  onConcurrencyChange,
}) => {
  // CTV-457 follow-up: drives "Pick from library" visibility and feeds the
  // modal. Filters out voided-only items. The button is only rendered when
  // there's at least one reusable commercial — first-time advertisers see
  // the upload-only UI as before.
  const { hasReusable, archived: archivedLibrary } = useReusableLibrary(
    slug,
    advertiserId ?? null,
  );
  const [pickerTarget, setPickerTarget] = React.useState<string | null>(null);
  const canPick = hasReusable && !!advertiserId;
  const handlePickerPick = (pick: LibraryPick) => {
    if (pickerTarget) onPickFromLibrary?.(pickerTarget, pick);
    setPickerTarget(null);
  };

  // CTV-526: upload-collision prompt. When a fresh File pick at a slot
  // matches an archived tile by (filename, size_bytes), divert into the
  // modal before the upload starts. Both modal branches end with the
  // archive removed so the file is back in the active library.
  const [collision, setCollision] = React.useState<null | {
    slotId: string;
    file: File;
    archivedStoragePath: string;
    archivedMimeType: string | null;
  }>(null);
  const handleFileChangeWithCollisionCheck = React.useCallback(
    (slotId: string, file: File | null) => {
      if (!file || !advertiserId) {
        onFileChange(slotId, file);
        return;
      }
      const match = archivedLibrary.find(
        (a) => a.filename === file.name && (a.size_bytes ?? 0) === file.size,
      );
      if (!match) {
        onFileChange(slotId, file);
        return;
      }
      setCollision({
        slotId,
        file,
        archivedStoragePath: match.preview_storage_path,
        archivedMimeType: match.mime_type,
      });
    },
    [advertiserId, archivedLibrary, onFileChange],
  );

  // CTV-653: the shared WaymarkPanel is form-context-free; the wizard hoists
  // the businessWebsite form binding here and passes it down as props.
  const { watch, setValue } = useFormContext<SelfServiceFormData>();
  const businessWebsite = (watch('businessWebsite') ?? '').trim();

  // Waymark commit bridge. The user explicitly commits via "Run this
  // commercial" (handleRunWaymark below, or the disclosure row's pill).
  // CTV-775: the context is null when no AI commercial is active on the
  // surface (every one is committed, or none exists yet). CTV-785: a
  // committed commercial is re-edited through the side editor session below,
  // never through this provider.
  const waymark = useWaymarkOptional();
  const renderedUrl = waymark?.renderedUrl ?? null;
  const videoId = waymark?.videoId ?? null;
  const activeCommercialId = waymark?.commercialId ?? null;
  const [isCommitting, setIsCommitting] = React.useState(false);

  // CTV-785: the finished commercials on this surface (ever in the campaign,
  // or rendered), joined to the slots carrying them right now. A slot the
  // buyer removed leaves its commercial on the receipt with Add to campaign.
  const finished = aiCommercials?.finished ?? EMPTY_FINISHED;
  const receipt = React.useMemo(() => buildReceipt(finished, slots), [finished, slots]);
  const activeCommitted =
    activeCommercialId != null &&
    slots.some(
      (s) => s.waymarkCommercialId === activeCommercialId && (!!s.storagePath || !!s.filename),
    );
  const allSettled = aiCommercials?.allSettled ?? false;

  // CTV-785 Add to campaign: a finished commercial no slot carries goes back
  // through the same commit path as Run this commercial (fetch its Waymark
  // render, ad-spec gate, stamp a slot).
  const [addingCommercialId, setAddingCommercialId] = React.useState<string | null>(null);
  const handleAddToCampaign = React.useCallback(
    async (commercialId: string) => {
      const entry = receipt.find((r) => r.commercialId === commercialId);
      if (!entry?.renderedUrl || !onCommitWaymark || addingCommercialId) return;
      setAddingCommercialId(commercialId);
      try {
        const res = await fetch(entry.renderedUrl);
        if (!res.ok) {
          console.warn('[waymark] add to campaign: fetch returned', res.status);
          return;
        }
        const blob = await res.blob();
        onCommitWaymark(blob, entry.videoId ?? commercialId, commercialId);
      } catch (e) {
        console.warn('[waymark] add to campaign failed', e);
      } finally {
        setAddingCommercialId(null);
      }
    },
    [receipt, onCommitWaymark, addingCommercialId],
  );

  // CTV-785: Review on a committed slot opens a side editor session on that
  // commercial (its own SDK instance, its own account) and leaves the active
  // commercial's generation alone. The new broadcast file replaces the slot
  // that carries the commercial, through `onReplaceWaymark`.
  const onReplaceWaymarkRef = React.useRef(onReplaceWaymark);
  onReplaceWaymarkRef.current = onReplaceWaymark;
  const editorSession = useWaymarkEditorSession({
    onFileReady: (file, commercialId) => onReplaceWaymarkRef.current?.(file, commercialId),
  });

  const handleRunWaymark = React.useCallback(async () => {
    if (!renderedUrl || !videoId) return;
    if (!onCommitWaymark) return;
    setIsCommitting(true);
    try {
      const res = await fetch(renderedUrl);
      if (!res.ok) {
        console.warn('[waymark] commit: fetch returned', res.status);
        return;
      }
      const blob = await res.blob();
      onCommitWaymark(blob, videoId, activeCommercialId);
    } catch (e) {
      console.warn('[waymark] commit failed', e);
    } finally {
      setIsCommitting(false);
    }
  }, [renderedUrl, videoId, activeCommercialId, onCommitWaymark]);

  // CTV-785 Review on a slot. The active (uncommitted) commercial opens in
  // the provider's editor; a committed one opens in the side editor session,
  // by its own access token, so the active commercial's generation is never
  // interrupted. One editor at a time: ignored while a session is live.
  const openEditor = waymark?.openEditor;
  const handleCustomizeSlot = React.useCallback(
    (commercialId: string) => {
      if (commercialId === activeCommercialId && openEditor) {
        void openEditor();
        return;
      }
      if (editorSession.session) return;
      const token = aiCommercials?.tokenOf(commercialId);
      if (!token) return;
      void editorSession.openForCommercial({ commercialId, commercialToken: token, slug });
    },
    [activeCommercialId, openEditor, editorSession, aiCommercials, slug],
  );

  // A slot "exists" for the scheduler only once it has a creative in it.
  // Empty slots remain rendered as upload cards (so the user has somewhere to
  // put the next video) but are excluded from the rotation/gantt and don't
  // count toward weight rebalancing.
  const slotHasContent = (s: CreativeSlotState) => !!s.storagePath || !!s.filename;
  const visibleSlots = React.useMemo<CreativeSlotState[]>(
    () => slots.filter(slotHasContent),
    [slots],
  );
  const showScheduler = visibleSlots.length >= 2;
  const slot0HasContent = !!slots[0] && slotHasContent(slots[0]);

  // CTV-457: positional palette anchored on the station's brand colours.
  // The same array drives the colour dot on every slot card AND the gantt
  // bar in the Scheduler — they were drifting because each surface was
  // building its own palette. Build once here and pass down. Palette length
  // tracks visibleSlots so newly-committed slots pick up the next colour.
  const paletteSize = Math.max(visibleSlots.length, 6);
  const palette = React.useMemo(
    () => buildStationPalette(primaryColor, secondaryColor, paletteSize),
    [primaryColor, secondaryColor, paletteSize],
  );
  const slotColors = React.useMemo(
    () => Array.from({ length: visibleSlots.length }, (_, i) => palette[i % palette.length]),
    [palette, visibleSlots.length],
  );
  const visiblePosition = React.useCallback(
    (slotId: string) => visibleSlots.findIndex((s) => s.slotId === slotId),
    [visibleSlots],
  );

  const hasMultipleSlots = slots.length > 1;

  // Per-day creative coverage (ADR-0006). Computed from visibleSlots so empty
  // slots and pending-but-not-yet-uploaded slots don't count.
  const coverage = React.useMemo(
    () =>
      validateCreativeCoverage({
        windowStart: campaignStartDate,
        windowEnd: campaignEndDate,
        creatives: visibleSlots.map((s) => ({ startDate: s.startDate, endDate: s.endDate })),
      }),
    [campaignStartDate, campaignEndDate, visibleSlots],
  );
  React.useEffect(() => {
    onCoverageChange?.(coverage.uncoveredRanges);
  }, [coverage, onCoverageChange]);

  // ADR CTV-506: per-day concurrency cap. Same input set as coverage; lifted
  // separately so the wizard's Continue gate can require both.
  const concurrency = React.useMemo(
    () =>
      validateCreativeConcurrency({
        windowStart: campaignStartDate,
        windowEnd: campaignEndDate,
        creatives: visibleSlots.map((s) => ({ startDate: s.startDate, endDate: s.endDate })),
      }),
    [campaignStartDate, campaignEndDate, visibleSlots],
  );
  React.useEffect(() => {
    onConcurrencyChange?.(concurrency.overCapRanges);
  }, [concurrency, onConcurrencyChange]);

  return (
    <div className="-m-4 sm:-m-6 md:-m-8" style={accentVars(accentColor, primaryColor)}>
      <div className="px-6 sm:px-10 md:px-14 pt-6 pb-6">
        <StepHeader step={3} label="Commercial" accentColor={accentColor} />
      </div>

      <div className="px-6 sm:px-10 md:px-14 pb-10 space-y-6">
        {/* CTV-558: Upload → Waymark → Specs (was Specs → Waymark → Upload).
            Buyers should land on the upload affordance first; specs are a
            tail reference. */}

        {/* Single render loop — every slot, including slot 0, goes through
            CreativeSlotCard. With only one slot the card has no header chrome
            (no badge, no remove, no weight), so single-creative UX matches
            the legacy "primary uploader" surface. */}
        <div className="space-y-5">
          {slots.map((slot) => {
            const pos = visiblePosition(slot.slotId);
            const color = pos >= 0 ? slotColors[pos] : '#cbd5e1';
            return (
              <CreativeSlotCard
                key={slot.slotId}
                slot={slot}
                slug={slug}
                file={files[slot.slotId] ?? null}
                accentColor={accentColor}
                color={color}
                index={pos >= 0 ? pos : -1}
                removable={hasMultipleSlots}
                uploading={uploadState[slot.slotId]?.uploading ?? false}
                uploadPercent={uploadState[slot.slotId]?.percent ?? null}
                uploadError={uploadState[slot.slotId]?.error ?? null}
                uploadSucceeded={uploadState[slot.slotId]?.succeeded ?? false}
                onFileChange={(file) =>
                  handleFileChangeWithCollisionCheck(slot.slotId, file)
                }
                onClickThroughUrlChange={(next) => onSlotChange(slot.slotId, { clickThroughUrl: next })}
                onRemove={() => onSlotRemove(slot.slotId)}
                onPickFromLibrary={canPick ? () => setPickerTarget(slot.slotId) : undefined}
                onCustomize={
                  slot.waymarkCommercialId
                    ? () => handleCustomizeSlot(slot.waymarkCommercialId as string)
                    : undefined
                }
              />
            );
          })}
        </div>

        {/* "+ Add another commercial" — gated on slot 0 having content so the
            user can't stack empty upload cards. */}
        {slot0HasContent && (
          <button
            type="button"
            onClick={onAddSlot}
            className="inline-flex items-center gap-2 text-[12px] font-semibold tracking-tight text-slate-600 hover:text-slate-900 bg-white border border-dashed border-slate-300 hover:border-slate-500 rounded-full px-4 py-2 transition-colors"
          >
            <Plus className="w-3.5 h-3.5" />
            Add another commercial
          </button>
        )}

        {/* CTV-653: the always-visible panel became a collapsed-by-default
            disclosure (intentional behavior change). Generation still starts
            in the background via WaymarkAutoKickoff; the disclosure's teaser
            reflects its progress without expanding anything. */}
        <WaymarkDisclosure
          accentColor={accentColor}
          primaryColor={primaryColor}
          slug={slug}
          receipt={receipt}
          allSettled={allSettled}
          activeCommitted={activeCommitted}
          onCustomize={openEditor ? () => void openEditor() : undefined}
          onRun={handleRunWaymark}
          onMakeAnother={onMakeAnother ? () => void onMakeAnother() : undefined}
          isCommitting={isCommitting}
          isMakingAnother={aiCommercials?.isCreating ?? false}
        >
          <WaymarkPanel
            accentColor={accentColor}
            primaryColor={primaryColor}
            slug={slug}
            waymarkCommitted={activeCommitted}
            allSettled={allSettled}
            receipt={receipt}
            onAddToCampaign={(id) => void handleAddToCampaign(id)}
            addingCommercialId={addingCommercialId}
            onCustomizeCommitted={handleCustomizeSlot}
            reviewSession={editorSession.session}
            reviewError={editorSession.error}
            activeOrdinal={activeCommercialId ? aiCommercials?.ordinalOf(activeCommercialId) : undefined}
            publisherName={publisherName}
            rejection={waymarkRejection}
            onClearRejection={onClearWaymarkRejection}
            businessWebsite={businessWebsite}
            onBusinessWebsiteChange={(next) => setValue('businessWebsite', next, { shouldDirty: true })}
          />
        </WaymarkDisclosure>

        {/* CTV-558: Ad specifications — one compact reference line instead
            of the prior two-column card; sits below upload + Waymark so the
            buyer's primary action lands at the top. */}
        <div
          className="rounded-2xl border border-slate-200/80 bg-white overflow-hidden"
          style={{ boxShadow: 'var(--card-shadow)' }}
        >
          <div className="px-5 py-3 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-slate-500">
            <span className="text-[10px] font-medium uppercase tracking-[0.08em] text-slate-400 mr-1">
              Ad specs
            </span>
            {specs.map(({ label, value }, i) => (
              <React.Fragment key={label}>
                {i > 0 && <span aria-hidden className="text-slate-300">·</span>}
                <span>
                  <span className="text-slate-400">{label}: </span>
                  <span className="text-slate-700 font-medium">{value}</span>
                </span>
              </React.Fragment>
            ))}
          </div>
          <div
            className="px-5 py-2.5 border-t border-slate-200/70 flex items-start gap-2"
            style={{ backgroundColor: tintFromAccent(accentColor, 0.04) }}
          >
            <ShieldCheck className="w-3.5 h-3.5 mt-0.5 shrink-0" style={{ color: accentColor }} />
            <p className="text-[11px] text-slate-600 leading-relaxed">
              All commercials are reviewed before launch, typically within 24–48 hours.
            </p>
          </div>
        </div>

        {/* Rotation & schedule — only rendered for multi-commercial campaigns;
            sits at the bottom of the step so the upload affordance and
            ad-spec reference both land before the buyer thinks about
            scheduling. */}
        {showScheduler && (
          <MultiCreativeScheduler
            slots={visibleSlots}
            colors={slotColors}
            campaignStartDate={campaignStartDate}
            campaignEndDate={campaignEndDate}
            accentColor={accentColor}
            slug={slug}
            onSlotChange={onSlotChange}
            onResetWeights={onResetWeights}
          />
        )}

        {visibleSlots.length > 0 && coverage.uncoveredRanges.length > 0 && (
          <CoverageBanner
            uncoveredRanges={coverage.uncoveredRanges}
            surface="wizard"
            slots={visibleSlots.map((s) => ({
              id: s.slotId,
              filename: s.filename,
              startDate: s.startDate,
              endDate: s.endDate,
            }))}
            onExtendLeftCreative={(id, newEnd) => onSlotChange(id, { endDate: newEnd })}
            onExtendRightCreative={(id, newStart) => onSlotChange(id, { startDate: newStart })}
          />
        )}
        {visibleSlots.length > 0 && concurrency.overCapRanges.length > 0 && (
          <ConcurrencyBanner overCapRanges={concurrency.overCapRanges} />
        )}
      </div>

      {advertiserId && (
        <LibraryPickerModal
          open={pickerTarget !== null}
          slug={slug}
          advertiserId={advertiserId}
          onClose={() => setPickerTarget(null)}
          onPick={handlePickerPick}
        />
      )}

      {collision && advertiserId && (
        <ArchivedFileCollisionModal
          slug={slug}
          advertiserId={advertiserId}
          filename={collision.file.name}
          sizeBytes={collision.file.size}
          archivedStoragePath={collision.archivedStoragePath}
          archivedMimeType={collision.archivedMimeType}
          accentColor={accentColor}
          onClose={() => setCollision(null)}
          onReuseArchived={(info) => {
            // Skip the upload entirely: feed the archived storage path to the
            // slot via the LibraryPick path, exactly like a Library Picker
            // selection. The wizard's onPickFromLibrary collapses to
            // applyLibraryPick under the hood.
            if (onPickFromLibrary) {
              onPickFromLibrary(collision.slotId, {
                storagePath: info.storagePath,
                filename: info.filename,
                mimeType: info.mimeType,
                sizeBytes: info.sizeBytes,
                clickThroughUrl: '',
              });
            }
            setCollision(null);
          }}
          onUploadAnyway={() => {
            onFileChange(collision.slotId, collision.file);
            setCollision(null);
          }}
        />
      )}
    </div>
  );
};


export default UploadCreativeStep;
